// src/components/customers/CustomerCardList.jsx
// Customer cards in a grid layout with inline editing

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// Validation popup component
const ValidationPopup = ({ message, onClose }) => {
  const popupRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div
        ref={popupRef}
        className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4"
      >
        <p className="text-gray-700 text-sm">{message}</p>
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#00aef7] text-white rounded-md hover:bg-[#0095d6]"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Review Request Modal
const ReviewRequestModal = ({ customer, onClose, userId }) => {
  const [subject, setSubject] = useState('Service Review Request');
  const [message, setMessage] = useState('Loading message...');
  const [isLoading, setIsLoading] = useState(true);
  const [senderInfo, setSenderInfo] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get('/api/get-user-name', {
          params: { userId },
          withCredentials: true
        });
        const d = response.data;
        const fullName = `${d.firstName} ${d.lastName}`;
        setSenderInfo(d);
        const cityStateZip = [d.city, d.state].filter(Boolean).join(', ') + (d.zip ? ` ${d.zip}` : '');
        const lines = [
          fullName,
          d.businessName,
          d.address,
          cityStateZip.trim(),
          d.phone,
          d.email,
        ].filter(Boolean).join('\n');
        setMessage(
          `Hi ${customer.customer_firstname},\n\nWe would appreciate your feedback about our recent service. Please take a moment to share your experience.\n\nThank you!\n${lines}`
        );
      } catch (error) {
        console.error('Error fetching user data:', error);
        setMessage(
          `Dear ${customer.customer_firstname},\n\nWe would appreciate your feedback about our recent service.\n\nThank you!\nOur Team`
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [userId, customer.customer_firstname]);

  const handleSend = async () => {
    try {
      setIsLoading(true);
      await axios.post('/api/auth/send-review-request', {
        customer_id: customer.customer_id,
        userId,
        subject,
        message
      }, { withCredentials: true });

      if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
        window.dispatchEvent(new Event("reviewStatsUpdated"));
      }

      onClose();
    } catch (error) {
      console.error('Failed to send review request:', error);
      alert('Failed to send request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-2xl p-6">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-semibold">Request Review</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={isLoading}
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
            <div className="p-2 bg-gray-50 rounded border">
              {customer.customer_firstname} {customer.customer_lastname} &lt;{customer.customer_email}&gt;
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-[#00aef7]"
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message:</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-[#00aef7] font-mono text-sm"
              disabled={isLoading}
            />
          </div>

        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            className="px-4 py-2 bg-[#00aef7] text-white rounded-md hover:bg-[#0095d6] disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Request'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Editable field component
const EditableField = ({
  value,
  field,
  placeholder,
  onSave,
  type = 'text',
  required = false,
  formatOnChange,
  className = '',
  labelClassName = '',
  inputClassName = '',
  truncate = false,
  autoEdit = false,
  onTabNext,
}) => {
  const [isEditing, setIsEditing] = useState(autoEdit);
  const [localValue, setLocalValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  // When autoEdit becomes true (e.g. tab advancing to this field), enter edit mode
  useEffect(() => {
    if (autoEdit) setIsEditing(true);
  }, [autoEdit]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    setIsEditing(true);
    setError(null);
  };

  const handleChange = (e) => {
    let newValue = e.target.value;
    if (formatOnChange) {
      newValue = formatOnChange(newValue);
    }
    setLocalValue(newValue);
  };

  const handleBlur = async () => {
    const trimmedValue = localValue.trim();
    const originalValue = (value || '').trim();

    if (required && !trimmedValue) {
      setError(`Required`);
      setLocalValue(originalValue);
      setIsEditing(false);
      setTimeout(() => setError(null), 2000);
      return;
    }

    if (trimmedValue === originalValue) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(field, trimmedValue);
      setIsEditing(false);
      setError(null);
    } catch (err) {
      setError('Failed');
      setLocalValue(originalValue);
      setTimeout(() => setError(null), 2000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current?.blur();
    }
    if (e.key === 'Tab' && onTabNext) {
      e.preventDefault();
      // Save current field, then advance to next
      const trimmedValue = localValue.trim();
      onSave(field, trimmedValue);
      setIsEditing(false);
      onTabNext();
    }
    if (e.key === 'Escape') {
      setLocalValue(value || '');
      setIsEditing(false);
      setError(null);
    }
  };

  if (isEditing) {
    return (
      <div className={`relative ${truncate ? 'w-full' : 'inline-block'} ${className}`}>
        <input
          ref={inputRef}
          type={type}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`border border-[#00aef7] rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#00aef7] ${isSaving ? 'bg-gray-100' : 'bg-white'} ${inputClassName}`}
          disabled={isSaving}
        />
      </div>
    );
  }

  return (
    <span className={`relative ${truncate ? 'block min-w-0 truncate' : 'inline-block'} ${className}`}>
      <span
        onClick={handleClick}
        className={`cursor-pointer hover:bg-[#00aef7]/20 rounded px-0.5 transition-colors ${truncate ? 'truncate block' : ''} ${labelClassName} ${error ? 'text-red-500' : ''}`}
        title={value || 'Click to edit'}
      >
        {error || value || <span className="text-gray-400 italic text-xs">{placeholder}</span>}
      </span>
    </span>
  );
};

// Phone formatter
const formatPhone = (value) => {
  const raw = value.replace(/\D/g, '');
  return raw.replace(/^(\d{3})(\d{0,3})(\d{0,4}).*/, (match, a, b, c) =>
    [a, b, c].filter(Boolean).join('-')
  );
};

// Single Customer Card
const CustomerCard = ({
  customer,
  userId,
  businessId,
  onUpdate,
  onRequestReview,
  onSaveNew,
  onCancelNew,
  isNew = false
}) => {
  const [localData, setLocalData] = useState({
    firstName: customer.customer_firstname || '',
    lastName: customer.customer_lastname || '',
    email: customer.customer_email || '',
    zip: customer.customer_zipcode || '',
    phone: customer.customer_phone || ''
  });
  const [showSuccess, setShowSuccess] = useState(false);

  // Tab order refs for new card fields
  const [editingField, setEditingField] = useState(isNew ? 'firstName' : null);
  const fieldOrder = ['firstName', 'lastName', 'email', 'zip', 'phone'];
  const advanceToField = (currentField) => {
    const idx = fieldOrder.indexOf(currentField);
    if (idx < fieldOrder.length - 1) {
      setEditingField(fieldOrder[idx + 1]);
    } else {
      setEditingField(null); // last field, close editing
    }
  };

  const handleFieldSave = async (field, value) => {
    // Update local state immediately
    setLocalData(prev => ({ ...prev, [field]: value }));

    if (isNew) {
      // For new customers, just update local state - don't save yet
      return;
    }

    // For existing customers, save immediately
    const fieldMap = {
      firstName: 'customer_firstname',
      lastName: 'customer_lastname',
      email: 'customer_email',
      zip: 'customer_zipcode',
      phone: 'customer_phone'
    };

    await axios.put(`/api/customer-cards/${customer.customer_id}`, {
      [field]: value
    }, { withCredentials: true });

    // Notify parent of update
    if (onUpdate) {
      onUpdate(customer.customer_id, fieldMap[field], value);
    }

    // Show success briefly
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 1500);
  };

  const handleSaveNewCustomer = async () => {
    // Validate
    if (!localData.firstName || !localData.lastName || !localData.email || !localData.zip) {
      return;
    }

    await onSaveNew(customer.customer_id, {
      customer_firstname: localData.firstName,
      customer_lastname: localData.lastName,
      customer_email: localData.email,
      customer_zipcode: localData.zip,
      customer_phone: localData.phone
    });
  };

  const canSaveNew = localData.firstName && localData.lastName && localData.email && localData.zip;

  return (
    <div
      className={`
        relative rounded-lg p-3 bg-white
        ${isNew ? 'ring-2 ring-[#00aef7]/50' : ''}
        transition-all duration-200 hover:translate-y-[-1px]
      `}
      style={{
        boxShadow: isNew
          ? '0 4px 12px -2px rgba(0, 174, 247, 0.3), 0 2px 4px -1px rgba(0, 174, 247, 0.2), inset 0 -8px 12px -8px rgba(0, 174, 247, 0.15)'
          : '0 4px 12px -2px rgba(0, 174, 247, 0.25), 0 2px 4px -1px rgba(0, 174, 247, 0.15), inset 0 -8px 12px -8px rgba(0, 174, 247, 0.1)',
        border: '1px solid rgba(0, 174, 247, 0.2)',
        borderBottomColor: 'rgba(0, 174, 247, 0.35)',
        borderBottomWidth: '2px'
      }}
    >
      {/* Success indicator */}
      {showSuccess && (
        <div className="absolute top-1 right-1 text-green-500 text-xs flex items-center gap-0.5 bg-white/80 rounded px-1">
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}

      {/* Action buttons for existing customers */}
      {!isNew && (
        <div className="absolute top-1 right-1 flex space-x-0.5">
          <button
            onClick={() => onRequestReview?.(customer)}
            className="p-1 rounded hover:bg-white/60 transition-colors"
            title="Request Review"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-yellow-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 8l7.89 5.26a2 2 0 0 0 2.22 0L21 8M5 19h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z"
              />
            </svg>
          </button>
        </div>
      )}

      {/* New customer action buttons */}
      {isNew && (
        <div className="absolute top-1 right-1 flex space-x-1">
          <button
            onClick={handleSaveNewCustomer}
            disabled={!canSaveNew}
            className={`p-1 rounded transition-colors ${canSaveNew ? 'hover:bg-green-100 text-green-600' : 'text-gray-300 cursor-not-allowed'}`}
            title="Save Customer"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </button>
          <button
            onClick={() => onCancelNew?.(customer.customer_id)}
            className="p-1 rounded hover:bg-red-100 text-red-500 transition-colors"
            title="Cancel"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Customer Name */}
      <div className="flex items-center gap-0.5 mb-1 pr-10 min-w-0 overflow-hidden whitespace-nowrap">
        <EditableField
          value={localData.firstName}
          field="firstName"
          placeholder="First"
          onSave={handleFieldSave}
          required={true}
          labelClassName="font-semibold text-gray-800 text-sm"
          inputClassName="w-28"
          className="shrink-0 max-w-[48%]"
          truncate={true}
          autoEdit={isNew && editingField === 'firstName'}
          onTabNext={isNew ? () => advanceToField('firstName') : undefined}
        />
        <EditableField
          value={localData.lastName}
          field="lastName"
          placeholder="Last"
          onSave={handleFieldSave}
          required={true}
          labelClassName="font-semibold text-gray-800 text-sm"
          inputClassName="w-28"
          className="min-w-0 max-w-[48%]"
          truncate={true}
          autoEdit={isNew && editingField === 'lastName'}
          onTabNext={isNew ? () => advanceToField('lastName') : undefined}
        />
      </div>

      {/* Email — truncate with tooltip for long addresses */}
      <div className="mb-1 min-w-0 overflow-hidden pr-2">
        <EditableField
          value={localData.email}
          field="email"
          placeholder="Email"
          type="email"
          onSave={handleFieldSave}
          required={true}
          labelClassName="text-xs text-gray-600"
          inputClassName="w-full max-w-[200px]"
          truncate={true}
          autoEdit={isNew && editingField === 'email'}
          onTabNext={isNew ? () => advanceToField('email') : undefined}
        />
      </div>

      {/* Zip and Phone */}
      <div className="flex items-center gap-3 text-xs">
        <div className="flex items-center gap-0.5">
          <span className="text-gray-500">Zip:</span>
          <EditableField
            value={localData.zip}
            field="zip"
            placeholder="Zip"
            onSave={handleFieldSave}
            required={true}
            labelClassName="text-gray-700"
            inputClassName="w-16"
            autoEdit={isNew && editingField === 'zip'}
            onTabNext={isNew ? () => advanceToField('zip') : undefined}
          />
        </div>
        <div className="flex items-center gap-0.5">
          <span className="text-gray-500">Ph:</span>
          <EditableField
            value={localData.phone}
            field="phone"
            placeholder="--"
            onSave={handleFieldSave}
            formatOnChange={formatPhone}
            labelClassName="text-gray-700"
            inputClassName="w-24"
            autoEdit={isNew && editingField === 'phone'}
            onTabNext={isNew ? () => advanceToField('phone') : undefined}
          />
        </div>
      </div>

      {/* New customer hint */}
      {isNew && (
        <div className="mt-2 pt-1 border-t border-[#00aef7]/20 text-[10px] text-[#00aef7]">
          Click fields to edit. Save when done.
        </div>
      )}
    </div>
  );
};

// Main CustomerCardList Component
const CustomerCardList = ({
  userId,
  businessId,
  customers,
  setCustomers,
  userFirstName,
  userLastName,
  fetchReviews
}) => {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [popup, setPopup] = useState({ show: false, message: '' });

  const handleUpdate = (customerId, field, value) => {
    setCustomers(prev =>
      prev.map(c =>
        c.customer_id === customerId
          ? { ...c, [field]: value }
          : c
      )
    );
  };

  const handleSaveNewCustomer = async (tempId, data) => {
    try {
      // Validate
      if (!data.customer_firstname || !data.customer_lastname || !data.customer_email || !data.customer_zipcode) {
        setPopup({
          show: true,
          message: 'Please fill in all required fields: First Name, Last Name, Email, and Zip Code.'
        });
        return;
      }

      const response = await axios.post('/api/customer-cards', {
        u_id: userId,
        b_id: businessId,
        customer_firstname: data.customer_firstname,
        customer_lastname: data.customer_lastname,
        customer_email: data.customer_email,
        customer_zipcode: data.customer_zipcode,
        customer_phone: data.customer_phone || ''
      });

      const createdCustomer = response.data;

      // Replace temp customer with real one
      setCustomers(prev => [
        createdCustomer,
        ...prev.filter(c => c.customer_id !== tempId)
      ]);

    } catch (error) {
      console.error('Failed to save new customer:', error);
      setPopup({
        show: true,
        message: 'Failed to save customer. Please try again.'
      });
    }
  };

  const handleCancelNewCustomer = (tempId) => {
    setCustomers(prev => prev.filter(c => c.customer_id !== tempId));
  };

  // Filter out "Imported Reviews" placeholder
  const displayCustomers = customers.filter(customer => {
    return !(
      customer.customer_firstname === "Imported" &&
      customer.customer_lastname === "Reviews"
    );
  });

  return (
    <div className="max-h-[500px] -mt-10 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-transparent overscroll-contain pr-1">
      {/* Grid of cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
        {displayCustomers.map(customer => (
          <CustomerCard
            key={customer.customer_id}
            customer={customer}
            userId={userId}
            businessId={businessId}
            isNew={String(customer.customer_id).startsWith('new_')}
            onUpdate={handleUpdate}
            onRequestReview={setSelectedCustomer}
            onSaveNew={handleSaveNewCustomer}
            onCancelNew={handleCancelNewCustomer}
          />
        ))}
      </div>

      {/* Empty state */}
      {displayCustomers.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>No customers yet.</p>
          <p className="text-sm mt-1">Click + to add your first customer.</p>
        </div>
      )}

      {/* Review Request Modal */}
      {selectedCustomer && (
        <ReviewRequestModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          userId={userId}
        />
      )}

      {/* Validation Popup */}
      {popup.show && (
        <ValidationPopup
          message={popup.message}
          onClose={() => setPopup({ show: false, message: '' })}
        />
      )}
    </div>
  );
};

export default CustomerCardList;
